import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeader from "../SectionHeader";
import { FaBuilding, FaGift, FaPlug, FaArrowLeft, FaCheck, FaStar, FaRocket, FaHandshake, FaCog } from "react-icons/fa";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/SupportSection-fa.json";
import en from "./locales/SupportSection-en.json";
import ar from "./locales/SupportSection-ar.json";
import tr from "./locales/SupportSection-tr.json";

const SupportSection: React.FC = () => {
    const { t, i18n } = useTranslation();

    // Register component translations
    useEffect(() => {
        const ns = "SupportSection";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [activeCard, setActiveCard] = useState<number | null>(null);
    const [floatingParticles, setFloatingParticles] = useState<Array<{ id: number; x: number; y: number }>>([]);

    // Determine text direction based on current language
    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";
    const textAlign = isRTL ? "text-right" : "text-left";

    const iconMap = [FaBuilding, FaGift, FaPlug];
    const colorMap = ["#FF5722", "#0072CE", "#2E8B57"];
    const gradientMap = ["from-orange-500 to-red-500", "from-blue-500 to-cyan-500", "from-green-500 to-emerald-500"];
    const bgGradientMap = ["from-orange-50 to-red-50", "from-blue-50 to-cyan-50", "from-green-50 to-emerald-50"];
    const accentColorMap = ["orange", "blue", "green"];

    const supportCards = Array.from({ length: 3 }, (_, index) => {
        const Icon = iconMap[index];
        const featuresData = [];
        for (let i = 0; i < 10; i++) {
            const feature = t(`cards.${index}.features.${i}`, { ns: "SupportSection", defaultValue: "" });
            if (feature && feature !== `cards.${index}.features.${i}`) {
                featuresData.push(feature);
            } else {
                break;
            }
        }

        return {
            id: index + 1,
            icon: <Icon className="text-2xl" />,
            title: t(`cards.${index}.title`, { ns: "SupportSection" }),
            description: t(`cards.${index}.description`, { ns: "SupportSection" }),
            features: featuresData,
            buttonText: t(`cards.${index}.buttonText`, { ns: "SupportSection" }),
            color: colorMap[index],
            gradient: gradientMap[index],
            bgGradient: bgGradientMap[index],
            accentColor: accentColorMap[index],
            url: t(`cards.${index}.url`, { ns: "SupportSection", defaultValue: "" }) || t(`cards.${index}.whatsappUrl`, { ns: "SupportSection" }),
        };
    });

    useEffect(() => {
        const particles = Array.from({ length: 15 }, (_, i) => ({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
        }));
        setFloatingParticles(particles);
    }, []);

    const handleCardClick = (id: number, url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <section dir={dir} className="pt-24 relative overflow-hidden pb-10 md:pb-20">
            <div className="absolute inset-0 "></div>

            <div className="absolute inset-0 overflow-hidden">
                {floatingParticles.map((particle) => (
                    <motion.div
                        key={particle.id}
                        className="absolute w-2 h-2 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full"
                        style={{
                            left: `${particle.x}%`,
                            top: `${particle.y}%`,
                        }}
                        animate={{
                            y: [0, -20, 0],
                            x: [0, 10, 0],
                            opacity: [0.3, 0.8, 0.3],
                            scale: [1, 1.2, 1],
                        }}
                        transition={{
                            duration: 4 + Math.random() * 2,
                            repeat: Infinity,
                            delay: Math.random() * 2,
                            ease: "easeInOut",
                        }}
                    />
                ))}
            </div>

            <motion.div
                className="absolute top-20 left-1/4 w-96 h-96 bg-gradient-to-r from-orange-200/30 to-red-200/20 rounded-full blur-3xl"
                animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            />
            <motion.div
                className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-r from-blue-200/30 to-cyan-200/20 rounded-full blur-3xl"
                animate={{
                    scale: [1.1, 1, 1.1],
                    opacity: [0.4, 0.6, 0.4],
                }}
                transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                }}
            />

            <div className="container mx-auto px-4 sm:px-10 relative z-10">
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}>
                    <SectionHeader
                        regularText={t("header.regularText", { ns: "SupportSection" })}
                        highlightedText={t("header.highlightedText", { ns: "SupportSection" })}
                        descriptionHighlight={t("header.descriptionHighlight", { ns: "SupportSection" })}
                        descriptionText={t("header.descriptionText", { ns: "SupportSection" })}
                        className="pb-16"
                    />
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                    {supportCards.map((card, index) => (
                        <motion.div
                            key={card.id}
                            className="group relative"
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.6,
                                delay: index * 0.2,
                                ease: "easeOut",
                            }}
                            onHoverStart={() => setActiveCard(card.id)}
                            onHoverEnd={() => setActiveCard(null)}
                            whileHover={{ y: -8, scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <div className="relative h-full bg-white/95 backdrop-blur-xl rounded-3xl p-8 border border-slate-100 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden">
                                <motion.div
                                    className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} opacity-0 rounded-3xl`}
                                    animate={{
                                        opacity: activeCard === card.id ? 0.4 : 0,
                                    }}
                                    transition={{ duration: 0.4 }}
                                />

                                <motion.div
                                    className={`absolute inset-0 rounded-3xl border-2 border-transparent bg-gradient-to-r ${card.gradient} opacity-0`}
                                    style={{
                                        mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                                        maskComposite: "exclude",
                                    }}
                                    animate={{
                                        opacity: activeCard === card.id ? 0.6 : 0,
                                    }}
                                    transition={{ duration: 0.4 }}
                                />

                                <AnimatePresence>
                                    {activeCard === card.id && (
                                        <div className="absolute inset-0 pointer-events-none">
                                            {[...Array(12)].map((_, i) => (
                                                <motion.div
                                                    key={i}
                                                    className="absolute w-1.5 h-1.5 rounded-full"
                                                    style={{ backgroundColor: card.color }}
                                                    initial={{
                                                        opacity: 0,
                                                        scale: 0,
                                                        x: `${20 + Math.random() * 60}%`,
                                                        y: `${20 + Math.random() * 60}%`,
                                                    }}
                                                    animate={{
                                                        opacity: [0, 0.8, 0],
                                                        scale: [0, 1.5, 0],
                                                        y: [0, -30 - i * 5],
                                                        x: [0, (i % 2 === 0 ? 1 : -1) * (10 + Math.random() * 20)],
                                                    }}
                                                    exit={{ opacity: 0, scale: 0 }}
                                                    transition={{
                                                        duration: 2.5,
                                                        repeat: Infinity,
                                                        delay: i * 0.2,
                                                        ease: "easeOut",
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </AnimatePresence>

                                <div className="relative z-10 h-full flex flex-col">
                                    <div className="mb-8 relative">
                                        <motion.div
                                            className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${card.gradient} text-white shadow-2xl relative overflow-hidden`}
                                            whileHover={{
                                                rotate: [0, -5, 5, 0],
                                                scale: 1.1,
                                            }}
                                            transition={{
                                                rotate: { duration: 0.6 },
                                                scale: { duration: 0.3 },
                                            }}
                                        >
                                            <motion.div
                                                className="absolute inset-0 bg-white/20 rounded-2xl"
                                                animate={{
                                                    scale: activeCard === card.id ? [1, 1.2, 1] : 1,
                                                    opacity: activeCard === card.id ? [0.3, 0.7, 0.3] : 0,
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: activeCard === card.id ? Infinity : 0,
                                                }}
                                            />

                                            <motion.div
                                                className="relative z-10"
                                                animate={{
                                                    scale: activeCard === card.id ? [1, 1.1, 1] : 1,
                                                }}
                                                transition={{
                                                    duration: 1.5,
                                                    repeat: activeCard === card.id ? Infinity : 0,
                                                }}
                                            >
                                                {card.icon}
                                            </motion.div>

                                            <motion.div
                                                className={`absolute top-1 ${isRTL ? "right-1" : "left-1"} w-6 h-6 bg-white/40 rounded-full blur-sm`}
                                                animate={{
                                                    opacity: activeCard === card.id ? [0, 1, 0] : 0,
                                                    scale: activeCard === card.id ? [0.5, 1.2, 0.5] : 0.5,
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: activeCard === card.id ? Infinity : 0,
                                                }}
                                            />
                                        </motion.div>

                                        <motion.div
                                            className={`absolute -top-2 ${isRTL ? "-right-2" : "-left-2"} bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-lg`}
                                            initial={{ scale: 0, opacity: 0 }}
                                            animate={{
                                                scale: activeCard === card.id ? 1 : 0,
                                                opacity: activeCard === card.id ? 1 : 0,
                                            }}
                                            transition={{ duration: 0.3, delay: 0.1 }}
                                        >
                                            <FaCheck className={`inline ${isRTL ? "mr-1" : "ml-1"}`} />
                                            {t("statusBadge", { ns: "SupportSection" })}
                                        </motion.div>
                                    </div>

                                    <motion.h3
                                        className={`text-2xl font-bold text-gray-900 mb-4 leading-tight relative ${textAlign}`}
                                        animate={{
                                            y: activeCard === card.id ? -2 : 0,
                                        }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {card.title}

                                        <motion.div
                                            className={`absolute -bottom-2 ${isRTL ? "left-0" : "right-0"} h-1 bg-gradient-to-r ${card.gradient} rounded-full`}
                                            initial={{ width: 0, opacity: 0 }}
                                            animate={{
                                                width: activeCard === card.id ? "100%" : "0%",
                                                opacity: activeCard === card.id ? 1 : 0,
                                            }}
                                            transition={{ duration: 0.5, ease: "easeOut" }}
                                        />
                                    </motion.h3>

                                    <motion.p
                                        className={`text-slate-700 leading-relaxed mb-6 text-base font-normal ${textAlign}`}
                                    >
                                        {card.description}
                                    </motion.p>

                                    <motion.div className="mb-8 space-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 + index * 0.1 }}>
                                        {card.features.map((feature, featureIndex) => (
                                            <motion.div
                                                key={featureIndex}
                                                className={`flex items-center gap-3 text-sm text-slate-800 font-medium ${isRTL ? "flex-row" : "flex-row-reverse justify-end"}`}
                                                initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{
                                                    delay: 0.4 + index * 0.1 + featureIndex * 0.1,
                                                    duration: 0.4,
                                                }}
                                                whileHover={{ x: isRTL ? -5 : 5, scale: 1.02 }}
                                            >
                                                <motion.div
                                                    className={`w-2 h-2 rounded-full bg-gradient-to-r ${card.gradient}`}
                                                    animate={{
                                                        scale: activeCard === card.id ? [1, 1.3, 1] : 1,
                                                    }}
                                                    transition={{
                                                        duration: 1.5,
                                                        repeat: activeCard === card.id ? Infinity : 0,
                                                        delay: featureIndex * 0.2,
                                                    }}
                                                />
                                                <span className={textAlign}>{feature}</span>
                                            </motion.div>
                                        ))}
                                    </motion.div>

                                    <div className="mt-auto">
                                        <motion.button
                                            onClick={() => handleCardClick(card.id, card.url)}
                                            className={`w-full group/btn relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r ${card.gradient} text-white rounded-2xl font-semibold text-base shadow-2xl hover:shadow-3xl transition-all duration-400 overflow-hidden`}
                                            whileHover={{
                                                scale: 1.03,
                                                y: -2,
                                                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
                                            }}
                                            whileTap={{ scale: 0.98 }}
                                            transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                            type="button"
                                        >
                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12"
                                                initial={{ x: "-100%" }}
                                                whileHover={{ x: "100%" }}
                                                transition={{ duration: 0.8, ease: "easeInOut" }}
                                            />

                                            <motion.div
                                                className="absolute inset-0 bg-white/10 rounded-2xl"
                                                animate={{
                                                    opacity: activeCard === card.id ? [0, 0.4, 0] : 0,
                                                }}
                                                transition={{
                                                    duration: 2,
                                                    repeat: activeCard === card.id ? Infinity : 0,
                                                }}
                                            />

                                            <span className="relative z-10">{card.buttonText}</span>

                                            <motion.div
                                                className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm"
                                                animate={{
                                                    x: activeCard === card.id ? (isRTL ? [0, -5, 0] : [0, 5, 0]) : 0,
                                                    rotate: activeCard === card.id ? (isRTL ? [0, 15, 0] : [0, -15, 0]) : 0,
                                                    scale: activeCard === card.id ? [1, 1.1, 1] : 1,
                                                }}
                                                transition={{
                                                    duration: 1.5,
                                                    repeat: activeCard === card.id ? Infinity : 0,
                                                    ease: "easeInOut",
                                                }}
                                                whileHover={{
                                                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                                                    scale: 1.1,
                                                }}
                                            >
                                                <FaArrowLeft className={`text-sm ${isRTL ? "rotate-180" : ""}`} />
                                            </motion.div>
                                        </motion.button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default SupportSection;
